import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import styles from "./pagination.module.css";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const Pagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className = "",
}) => {
  const { t: tCommon } = useTranslation("common");
  const displayPageSize = PAGE_SIZE_OPTIONS.includes(Number(pageSize))
    ? Number(pageSize)
    : PAGE_SIZE_OPTIONS[0];
  const totalPages = Math.ceil(total / displayPageSize);
  const safePage = Math.min(Math.max(Number(page) || 1, 1), totalPages || 1);
  const rangeStart = total > 0 ? (safePage - 1) * displayPageSize + 1 : 0;
  const rangeEnd = Math.min(safePage * displayPageSize, total);
  const [pageInput, setPageInput] = useState(String(safePage));

  useEffect(() => {
    setPageInput(String(safePage));
  }, [safePage]);

  useEffect(() => {
    if (total > 0 && Number(page) !== safePage) {
      onPageChange(1);
    }
  }, [onPageChange, page, safePage, total]);

  if (total <= 0) return null;

  const commitPageInput = () => {
    const parsedPage = Number.parseInt(pageInput, 10);
    const nextPage = Number.isNaN(parsedPage)
      ? safePage
      : Math.min(Math.max(parsedPage, 1), totalPages);

    setPageInput(String(nextPage));
    if (nextPage !== safePage) {
      onPageChange(nextPage);
    }
  };

  return (
    <div className={`${styles.pagination} ${className}`.trim()}>
      <span className={styles.range}>
        {tCommon("paginationRange", {
          start: rangeStart,
          end: rangeEnd,
          total,
          defaultValue: "Showing {{start}}-{{end}} of {{total}} items",
        })}
      </span>

      <div className={styles.controls}>
        <button
          className={styles.pageButton}
          onClick={() => onPageChange(1)}
          disabled={safePage === 1}
          type="button"
          aria-label={tCommon("First page")}
        >
          &laquo;
        </button>
        <button
          className={styles.pageButton}
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage === 1}
          type="button"
          aria-label={tCommon("Previous page")}
        >
          &lsaquo;
        </button>

        <span className={styles.info}>
          <Trans
            t={tCommon}
            i18nKey="paginationPage"
            defaults="Page <pageInput/> / {{totalPages}}"
            values={{ totalPages }}
            components={{
              pageInput: (
                <input
                  className={styles.pageInput}
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value)}
                  onBlur={commitPageInput}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.currentTarget.blur();
                    }
                  }}
                  aria-label={tCommon("Page number")}
                />
              ),
            }}
          />
        </span>

        <button
          className={styles.pageButton}
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage === totalPages}
          type="button"
          aria-label={tCommon("Next page")}
        >
          &rsaquo;
        </button>
        <button
          className={styles.pageButton}
          onClick={() => onPageChange(totalPages)}
          disabled={safePage === totalPages}
          type="button"
          aria-label={tCommon("Last page")}
        >
          &raquo;
        </button>
      </div>

      <label className={styles.pageSize}>
        <Trans
          t={tCommon}
          i18nKey="paginationPageSize"
          defaults="Per page <pageSizeSelect></pageSizeSelect> items"
          components={{
            pageSizeSelect: (
              <select
                value={displayPageSize}
                onChange={(event) =>
                  onPageSizeChange(Number(event.target.value))
                }
                aria-label={tCommon("Items per page")}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ),
          }}
        />
      </label>
    </div>
  );
};

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};
